import { apiFetch } from './api';
import type { Contact } from './ContactService';

const API_URL = import.meta.env.VITE_API_URL || '';

export interface Organization {
    organizacaoId: number;
    /** Razão social — é o nome que a listagem mostra. */
    nome: string;
    nomeFantasia?: string | null;
    /** Só dígitos; a máscara é daqui do front. */
    cnpj?: string | null;
    site?: string | null;
    email?: string | null;
    telefone?: string | null;
    segmento?: string | null;
    cep?: string | null;
    logradouro?: string | null;
    numero?: string | null;
    complemento?: string | null;
    bairro?: string | null;
    cidade?: string | null;
    estado?: string | null;
    observacao?: string | null;
    quantidadePessoas: number;
    dataCriacao: string;
    dataAtualizacao: string;
}

export interface OrganizationPayload {
    nome: string;
    nomeFantasia?: string | null;
    cnpj?: string | null;
    site?: string | null;
    email?: string | null;
    telefone?: string | null;
    segmento?: string | null;
    cep?: string | null;
    logradouro?: string | null;
    numero?: string | null;
    complemento?: string | null;
    bairro?: string | null;
    cidade?: string | null;
    estado?: string | null;
    observacao?: string | null;
}

/**
 * O que a verificação de duplicidade devolve. As duas metades têm pesos
 * diferentes, de propósito: CNPJ bloqueia o salvar, nome só avisa.
 * Ver docs/propostas/organizacoes.html §4.4.
 */
export interface DuplicidadeOrganizacao {
    bloqueioPorCnpj: Organization | null;
    avisoPorNome: Organization[];
}

export interface ApiResponse<T> {
    sucesso: boolean;
    mensagem: string;
    dados: T;
}

/** A API responde 400 com corpo quando recusa — a mensagem dela é a que vale. */
const ler = async <T,>(response: Response, erroPadrao: string): Promise<ApiResponse<T>> => {
    let corpo: ApiResponse<T> | null = null;
    try {
        corpo = await response.json();
    } catch {
        corpo = null;
    }
    if (!response.ok) {
        throw new Error(corpo?.mensagem || erroPadrao);
    }
    return corpo as ApiResponse<T>;
};

const JSON_HEADERS = { 'Content-Type': 'application/json' };

export const OrganizationService = {
    getAll: async (): Promise<ApiResponse<Organization[]>> => {
        const response = await apiFetch(`${API_URL}/api/Organizacao/BuscarTodas`, {
            method: 'GET',
            headers: JSON_HEADERS,
        });
        // Tenant sem nenhuma organização é estado normal, não erro.
        if (response.status === 400) {
            return { sucesso: true, mensagem: 'Sem organizações', dados: [] };
        }
        return ler<Organization[]>(response, 'Erro ao buscar organizações');
    },

    getById: async (id: number): Promise<ApiResponse<Organization>> => {
        const response = await apiFetch(`${API_URL}/api/Organizacao/BuscarPorId/${id}`, {
            method: 'GET',
            headers: JSON_HEADERS,
        });
        return ler<Organization>(response, 'Erro ao buscar a organização');
    },

    /** Busca por trecho do nome ou do CNPJ — alimenta o campo de seleção. */
    search: async (termo: string): Promise<Organization[]> => {
        const response = await apiFetch(
            `${API_URL}/api/Organizacao/Buscar?termo=${encodeURIComponent(termo)}`,
            { method: 'GET', headers: JSON_HEADERS },
        );
        if (response.status === 400) return [];
        const corpo = await ler<Organization[]>(response, 'Erro ao buscar organizações');
        return corpo.dados ?? [];
    },

    verificarDuplicidade: async (
        nome: string,
        cnpj: string | null,
        ignorarId?: number,
    ): Promise<DuplicidadeOrganizacao> => {
        const params = new URLSearchParams();
        if (nome) params.set('nome', nome);
        if (cnpj) params.set('cnpj', cnpj);
        if (ignorarId) params.set('ignorarId', String(ignorarId));

        const response = await apiFetch(
            `${API_URL}/api/Organizacao/VerificarDuplicidade?${params.toString()}`,
            { method: 'GET', headers: JSON_HEADERS },
        );
        const corpo = await ler<DuplicidadeOrganizacao>(response, 'Erro ao verificar duplicidade');
        return corpo.dados ?? { bloqueioPorCnpj: null, avisoPorNome: [] };
    },

    create: async (payload: OrganizationPayload): Promise<ApiResponse<Organization>> => {
        const response = await apiFetch(`${API_URL}/api/Organizacao/Criar`, {
            method: 'POST',
            headers: JSON_HEADERS,
            body: JSON.stringify(payload),
        });
        return ler<Organization>(response, 'Erro ao criar a organização');
    },

    update: async (id: number, payload: OrganizationPayload): Promise<ApiResponse<unknown>> => {
        const response = await apiFetch(`${API_URL}/api/Organizacao/Atualizar`, {
            method: 'PATCH',
            headers: JSON_HEADERS,
            body: JSON.stringify({ ...payload, organizacaoId: id }),
        });
        return ler<unknown>(response, 'Erro ao atualizar a organização');
    },

    remove: async (id: number): Promise<ApiResponse<unknown>> => {
        const response = await apiFetch(`${API_URL}/api/Organizacao/Remover/${id}`, {
            method: 'DELETE',
            headers: JSON_HEADERS,
        });
        return ler<unknown>(response, 'Erro ao remover a organização');
    },

    getContacts: async (id: number): Promise<Contact[]> => {
        const response = await apiFetch(`${API_URL}/api/Organizacao/${id}/Contatos`, {
            method: 'GET',
            headers: JSON_HEADERS,
        });
        if (response.status === 400) return [];
        const corpo = await ler<Contact[]>(response, 'Erro ao buscar as pessoas da organização');
        return corpo.dados ?? [];
    },

    linkContact: async (organizacaoId: number, contatoId: number): Promise<ApiResponse<unknown>> => {
        const response = await apiFetch(
            `${API_URL}/api/Organizacao/${organizacaoId}/VincularContato/${contatoId}`,
            { method: 'POST', headers: JSON_HEADERS },
        );
        return ler<unknown>(response, 'Erro ao vincular o contato');
    },

    unlinkContact: async (contatoId: number): Promise<ApiResponse<unknown>> => {
        const response = await apiFetch(
            `${API_URL}/api/Organizacao/DesvincularContato/${contatoId}`,
            { method: 'DELETE', headers: JSON_HEADERS },
        );
        return ler<unknown>(response, 'Erro ao desvincular o contato');
    },
};

/* ─── Formatação, no lugar único ────────────────────────────────── */

/** 12345678000190 → 12.345.678/0001-90. Incompleto sai como está. */
export const formatarCnpj = (cnpj?: string | null): string => {
    if (!cnpj) return '';
    const d = cnpj.replace(/\D/g, '').slice(0, 14);
    if (d.length !== 14) return d;
    return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
};

/** Máscara progressiva, para digitar no campo. */
export const mascararCnpj = (valor: string): string => {
    const d = valor.replace(/\D/g, '').slice(0, 14);
    if (d.length <= 2) return d;
    if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`;
    if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
    if (d.length <= 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
    return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
};

export const somenteDigitos = (valor?: string | null): string | null => {
    if (!valor) return null;
    const d = valor.replace(/\D/g, '');
    return d.length ? d : null;
};
